<script setup lang="ts">
import { IconChevronDown, IconCircleX } from '@tabler/icons-vue'
import Multiselect from '@vueform/multiselect'
import { computed, useTemplateRef } from 'vue'

import BaseIcon from '../BaseIcon.vue'

import { useComputedInput } from './helpers'
import type { SelectInputProps } from './types'

const emit = defineEmits(['update:modelValue'])
const props = withDefaults(defineProps<SelectInputProps>(), {
  searchable: true,
})
const input = useComputedInput(props, emit)

const multiselectRef = useTemplateRef<Multiselect>('multiselectElement')

/** aria-placeholder and aria-multiselectable are not supported on role="combobox"; we clear them and use native placeholder via attrs. */
const multiselectAria = computed(() => ({
  'aria-placeholder': undefined,
  'aria-multiselectable': undefined,
  /* v8 ignore next - selects should always have labels for accessibility, edge case not worth testing -- @preserve */
  'aria-label': props.label ?? undefined,
}))
const caretIcon = computed(() => props.rightIcon ?? IconChevronDown)
</script>

<template>
  <Multiselect
    ref="multiselectElement"
    :id="props.id"
    @open="multiselectRef?.focus()"
    :placeholder="props.placeholder"
    v-model="input"
    :close-on-select="true"
    :searchable="props.searchable"
    :groups="props.groups"
    :mode="props.mode"
    :options="props.options"
    :disabled="props.disabled || props.readOnly"
    :can-clear="!props.preventClearingSelection"
    :create-option="props.allowCreateOption"
    :append-new-option="false"
    :allow-absent="props.allowCreateOption"
    :open-direction="props.openDirection"
    @create="props.createOptionValidator"
    class="multi-select"
    :tabindex="props.tabindex"
    :aria="multiselectAria"
    :multiple-label="props.multipleLabel"
  >
    <template #caret="{ handleCaretClick, isOpen }">
      <span
        class="multiselect-caret-icon"
        :class="{ 'is-open': isOpen }"
        :style="{ pointerEvents: isOpen ? 'auto' : 'none' }"
        @click="handleCaretClick"
      >
        <BaseIcon class="multiselect-caret-base-icon" :icon="caretIcon" />
      </span>
    </template>
    <template #singlelabel="{ value: option }">
      <div class="multiselect-single-label">
        <BaseIcon v-if="option.icon" :icon="option.icon" class="multiselect-option-icon" />
        <span class="multiselect-single-label-text">{{ option.label }}</span>
      </div>
    </template>
    <template #option="{ option }">
      <BaseIcon v-if="option.icon" :icon="option.icon" class="multiselect-option-icon" />
      <span>{{ option.label }}</span>
    </template>
    <template #clear="{ clear }">
      <span
        class="multiselect-clear"
        role="button"
        tabindex="0"
        aria-label="Clear selection"
        data-clear
        @click="clear"
        @keyup.enter="clear"
      >
        <BaseIcon class="clear-icon" :icon="IconCircleX" :stroke="1" />
      </span>
    </template>
  </Multiselect>
</template>

<style scoped>
.multiselect-caret-base-icon {
  font-size: 14.667px;
}

.multiselect-clear {
  margin-right: 5px;
}

.clear-icon {
  font-size: 14.5px;
  color: var(--text-tertiary);
}
</style>
